import React, { useState } from 'react';
import KeywordFooter from '../components/KeywordFooter';

const TERMS_EMAIL = 'jimmy.lagattuta@gmail.com';

const Terms = () => {
  const effectiveDate = 'May 13, 2026';
  const [copiedEmail, setCopiedEmail] = useState(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(TERMS_EMAIL);
      setCopiedEmail(true);

      window.setTimeout(() => {
        setCopiedEmail(false);
      }, 2200);
    } catch (error) {
      console.error('Unable to copy terms contact email:', error);
      setCopiedEmail(false);
    }
  };

  const ContactEmailBox = ({ label = 'Contact Email' }) => {
    return (
      <div className="mt-4 rounded-2xl border border-cyan-300/15 bg-slate-950/60 px-4 py-3">
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300/80">
          {label}
        </p>

        <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
          <p className="break-all text-sm font-extrabold text-cyan-100">
            {TERMS_EMAIL}
          </p>

          <button
            type="button"
            onClick={handleCopyEmail}
            className="inline-flex w-fit items-center justify-center rounded-xl border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.12em] text-cyan-100 transition hover:-translate-y-0.5 hover:border-cyan-200/60 hover:bg-cyan-300/15 hover:text-white"
          >
            {copiedEmail ? 'Copied' : 'Copy Email'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <>
      <main className="min-h-screen bg-slate-950 px-6 py-16 text-white">
        <section className="mx-auto max-w-4xl rounded-3xl border border-cyan-300/20 bg-slate-900/70 p-6 shadow-2xl shadow-cyan-950/30 sm:p-8">
          <p className="text-sm font-bold uppercase tracking-[0.3em] text-cyan-300">
            SubtitleTsunami
          </p>

          <h1 className="mt-3 text-3xl font-black text-white sm:text-4xl">
            Terms of Service
          </h1>

          <p className="mt-3 text-sm font-bold text-slate-400">
            Effective Date: {effectiveDate}
          </p>

          <p className="mt-6 text-sm leading-7 text-slate-300">
            These Terms of Service govern your access to and use of
            SubtitleTsunami, a web-based video subtitle processing service that
            allows users to upload videos, generate subtitles, translate
            subtitle content, preview processed output, and download processed
            video files.
          </p>

          <p className="mt-4 text-sm leading-7 text-slate-300">
            By using SubtitleTsunami, you agree to these Terms. If you do not
            agree, do not use the service.
          </p>

          <div className="mt-8 space-y-8">
            <section>
              <h2 className="text-xl font-black text-white">
                1. Service Description
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami provides software tools for video subtitle
                processing. The service may allow users to upload a video,
                generate or process subtitles, translate subtitle text, preview
                processed files, and download the resulting video.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami is intended for creators, educators, businesses,
                marketers, and other users who process videos they own, created,
                licensed, or otherwise have permission to use.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                2. User Accounts
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Some features may require an account. You agree to provide
                accurate account information and to keep your login credentials
                secure. You are responsible for activity that occurs under your
                account.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may suspend or restrict access to accounts that violate these
                Terms, abuse the service, attempt to bypass usage limits, or use
                SubtitleTsunami in a way that creates legal, security, payment,
                or operational risk.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                3. Content Ownership and Upload Rights
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                You retain ownership of the videos, files, subtitles, and other
                content you upload or create through SubtitleTsunami, subject to
                any rights owned by third parties.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                You may only upload, subtitle, translate, process, preview, or
                download content that you own or have permission to use. You are
                solely responsible for ensuring that your uploaded content does
                not infringe any copyright, trademark, privacy right, publicity
                right, contractual right, or other legal right.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                By uploading content to SubtitleTsunami, you confirm that you
                have all rights, licenses, permissions, and consents necessary
                for SubtitleTsunami to process that content on your behalf.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                4. Limited License to Process Content
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                When you upload content, you grant SubtitleTsunami a limited,
                temporary, non-exclusive license to host, store, copy, process,
                transcribe, translate, subtitle, preview, and make the processed
                output available for download solely for the purpose of providing
                the service to you.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                This license is limited to operating, improving, securing, and
                supporting the service. It does not transfer ownership of your
                content to SubtitleTsunami.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                5. Prohibited Uses
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                You agree not to use SubtitleTsunami to upload, process, or
                distribute content that:
              </p>

              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-300">
                <li>You do not own, license, or have permission to use.</li>
                <li>
                  Infringes copyright, trademark, privacy, publicity, or other
                  rights.
                </li>
                <li>
                  Is illegal, fraudulent, deceptive, abusive, harassing,
                  defamatory, threatening, or harmful.
                </li>
                <li>Contains malware, viruses, spyware, or other harmful code.</li>
                <li>
                  Promotes illegal products, illegal services, or unlawful
                  activity.
                </li>
                <li>
                  Attempts to bypass security, usage limits, payment controls,
                  or technical restrictions.
                </li>
                <li>
                  Violates the rules, terms, or policies of any third-party
                  platform, payment provider, hosting provider, or service used
                  with SubtitleTsunami.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                6. Payments, Subscriptions, and Billing
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami may offer free, beta, paid, subscription, or
                usage-based plans. Pricing, plan limits, upload limits, video
                length limits, processing limits, and included features may be
                shown on the website, checkout page, or account area.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Payments may be processed by third-party payment providers such
                as Stripe. By purchasing a paid plan, you authorize us and our
                payment processor to charge the payment method you provide for
                applicable fees, taxes, renewals, and other charges related to
                your selected plan.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Subscription plans, if offered, may renew automatically unless
                canceled before the renewal date. You are responsible for
                canceling before renewal if you do not want future charges.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                7. Cancellations and Refunds
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                You may cancel a paid subscription according to the cancellation
                tools or instructions provided in the service, checkout flow, or
                account area.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Unless otherwise stated at checkout or required by law, payments
                for digital services, completed processing, used subscription
                periods, and usage-based charges are non-refundable. If you
                believe you were charged in error, contact us and we will review
                the issue in good faith.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Contact for billing questions:
              </p>

              <ContactEmailBox label="Billing Contact" />
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                8. Temporary Files and Downloads
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Uploaded and processed files may be temporary. Download links,
                previews, generated subtitles, and processed video files may
                expire or be deleted after a limited period for storage,
                security, and system optimization reasons.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                You are responsible for downloading and saving any processed
                files you want to keep before they expire.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                9. Accuracy of Subtitles and Translations
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami may use automated transcription, translation,
                and video processing tools. Automated output may contain errors,
                omissions, timing issues, mistranslations, formatting issues, or
                other inaccuracies.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                You are responsible for reviewing processed output before using,
                publishing, sharing, or distributing it. SubtitleTsunami does not
                guarantee that subtitles, translations, timing, formatting, or
                processed videos will be error-free or suitable for every use
                case.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                10. Service Availability
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We aim to keep SubtitleTsunami available and reliable, but we do
                not guarantee uninterrupted access. The service may be delayed,
                unavailable, limited, modified, or discontinued due to
                maintenance, technical issues, third-party provider issues,
                infrastructure limits, security concerns, or business reasons.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                11. Intellectual Property
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami, including its software, interface, branding,
                design, workflows, text, graphics, and other service materials,
                is owned by SubtitleTsunami or its owner and is protected by
                intellectual property laws.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                You may not copy, modify, reverse engineer, resell, scrape,
                reproduce, distribute, or create competing services from
                SubtitleTsunami materials except as allowed by law or with
                written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                12. Copyright Complaints
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami respects intellectual property rights. If you
                believe content processed through SubtitleTsunami infringes your
                copyright, please contact us with enough information to identify
                the copyrighted work, the allegedly infringing material, your
                contact information, and a statement that you believe in good
                faith that the use is unauthorized.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Copyright contact:
              </p>

              <ContactEmailBox label="Copyright Contact" />
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                13. Privacy
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Our Privacy Policy explains how we handle information related to
                accounts, uploads, processing events, logs, and service usage.
                Please review the Privacy Policy for more information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                14. Termination
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may suspend, restrict, or terminate access to SubtitleTsunami
                if we believe you violated these Terms, created risk for the
                service, abused the platform, failed to pay required fees, or
                used the service in a way that may expose us, other users, or
                third-party providers to liability.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                15. Disclaimers
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami is provided on an “as is” and “as available”
                basis. To the fullest extent permitted by law, we disclaim all
                warranties, express or implied, including warranties of
                merchantability, fitness for a particular purpose,
                non-infringement, accuracy, availability, and reliability.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                16. Limitation of Liability
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                To the fullest extent permitted by law, SubtitleTsunami and its
                owner will not be liable for indirect, incidental, special,
                consequential, exemplary, or punitive damages, or for loss of
                profits, revenue, data, content, goodwill, or business
                opportunity, arising from or related to your use of the service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                17. Changes to These Terms
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may update these Terms from time to time. When we update
                them, we may change the effective date above. Continued use of
                SubtitleTsunami after updated Terms are posted means you accept
                the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                18. Contact
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                For questions about these Terms, billing, refunds, copyright, or
                SubtitleTsunami, contact:
              </p>

              <ContactEmailBox />
            </section>
          </div>
        </section>
      </main>

      <KeywordFooter />
    </>
  );
};

export default Terms;